(() => {
  'use strict';

  const state = { activeDrawer: null, previousFocus: null, cartBusy: false };
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const drawerFor = (name) => document.querySelector(`[data-drawer="${name}"]`);

  function openDrawer(name) {
    const drawer = drawerFor(name);
    if (!drawer) return;
    if (state.activeDrawer && state.activeDrawer !== drawer) closeDrawer(state.activeDrawer, false);
    state.previousFocus = document.activeElement;
    state.activeDrawer = drawer;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    document.querySelectorAll(`[data-drawer-open="${name}"], ${name === 'cart-drawer' ? '[data-cart-toggle]' : '[data-unused]'}`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    window.setTimeout(() => {
      const target = drawer.querySelector('input:not([type="hidden"]), button:not([data-drawer-close]), a[href], [data-drawer-close]');
      if (target) target.focus();
    }, 60);
  }

  function closeDrawer(drawer = state.activeDrawer, restoreFocus = true) {
    if (!drawer) return;
    const name = drawer.dataset.drawer;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
    document.querySelectorAll(`[data-drawer-open="${name}"], ${name === 'cart-drawer' ? '[data-cart-toggle]' : '[data-unused]'}`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    state.activeDrawer = null;
    if (restoreFocus && state.previousFocus && document.contains(state.previousFocus)) state.previousFocus.focus();
  }

  function trapFocus(event) {
    if (!state.activeDrawer || event.key !== 'Tab') return;
    const items = [...state.activeDrawer.querySelectorAll(focusable)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function updateCartCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = count; });
  }

  async function getCart() {
    const response = await fetch(`${window.AmpedTheme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not load cart.');
    return response.json();
  }

  async function refreshCart({ open = false } = {}) {
    const [sectionResponse, cart] = await Promise.all([
      fetch(`${window.AmpedTheme.routes.cart}?section_id=cart-drawer`),
      getCart()
    ]);
    if (!sectionResponse.ok) throw new Error('Could not refresh cart.');
    const html = await sectionResponse.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector('#shopify-section-cart-drawer');
    const current = document.querySelector('#shopify-section-cart-drawer');
    if (incoming && current) current.replaceWith(incoming);
    updateCartCounts(cart.item_count);
    if (open) openDrawer('cart-drawer');
    return cart;
  }

  async function addToCart(form) {
    if (state.cartBusy) return;
    const submit = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-product-error]');
    state.cartBusy = true;
    if (submit) {
      submit.disabled = true;
      submit.classList.add('is-loading');
    }
    if (error) error.hidden = true;

    try {
      const response = await fetch(`${window.AmpedTheme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'This item could not be added.');
      await refreshCart({ open: true });
      const live = document.querySelector('#CartLiveRegion');
      if (live) live.textContent = `${data.product_title || 'Item'} added to cart.`;
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      } else {
        const live = document.querySelector('#CartLiveRegion');
        if (live) live.textContent = err.message;
      }
    } finally {
      state.cartBusy = false;
      if (submit) {
        submit.disabled = false;
        submit.classList.remove('is-loading');
      }
    }
  }

  async function changeCartLine(line, quantity) {
    if (state.cartBusy) return;
    state.cartBusy = true;
    try {
      const response = await fetch(`${window.AmpedTheme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Could not update cart.');
      await refreshCart({ open: true });
    } catch (err) {
      const live = document.querySelector('#CartLiveRegion');
      if (live) live.textContent = err.message;
    } finally {
      state.cartBusy = false;
    }
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.AmpedTheme.currency || 'USD'
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function updateVariant(selects) {
    const root = selects.closest('[data-product-section]');
    if (!root) return;
    const json = selects.querySelector('[data-variants-json]');
    if (!json) return;
    const variants = JSON.parse(json.textContent);
    const options = [...selects.querySelectorAll('[data-option-select]')].map((select) => select.value);
    const variant = variants.find((item) => item.options.every((value, index) => value === options[index]));
    const idInput = root.querySelector('[data-variant-id]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const addText = root.querySelector('[data-add-to-cart-text]');
    const price = root.querySelector('[data-product-price]');
    if (!variant) {
      if (addButton) addButton.disabled = true;
      if (addText) addText.textContent = 'Unavailable';
      return;
    }
    if (idInput) idInput.value = variant.id;
    if (price) price.textContent = formatMoney(variant.price);
    if (addButton) addButton.disabled = !variant.available;
    if (addText) addText.textContent = variant.available ? 'Add to cart' : 'Sold out';
    if (variant.featured_media) {
      const media = root.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (media) {
        root.querySelectorAll('[data-media-id]').forEach((item) => item.classList.toggle('is-active', item === media));
        if (window.matchMedia('(max-width: 749px)').matches) media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  async function loadRecommendations(element) {
    if (!element.dataset.url) return;
    try {
      const response = await fetch(element.dataset.url);
      if (!response.ok) return;
      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const replacement = parsed.querySelector('product-recommendations');
      if (replacement && replacement.innerHTML.trim()) element.innerHTML = replacement.innerHTML;
    } catch (_) {
      // Recommendations are optional; leave the section empty if unavailable.
    }
  }

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-drawer-open]');
    if (open) {
      event.preventDefault();
      openDrawer(open.dataset.drawerOpen);
      return;
    }
    const cartOpen = event.target.closest('[data-cart-toggle]');
    if (cartOpen) {
      event.preventDefault();
      openDrawer('cart-drawer');
      return;
    }
    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }
    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const item = quantityButton.closest('[data-cart-item]');
      const input = item && item.querySelector('[data-cart-quantity]');
      if (!item || !input) return;
      const quantity = Math.max(0, Number(input.value) + Number(quantityButton.dataset.quantityChange));
      changeCartLine(Number(item.dataset.line), quantity);
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      const item = remove.closest('[data-cart-item]');
      if (item) changeCartLine(Number(item.dataset.line), 0);
      return;
    }
    const productQuantity = event.target.closest('[data-product-quantity]');
    if (productQuantity) {
      const input = productQuantity.closest('.product-quantity').querySelector('[data-product-quantity-input]');
      input.value = Math.max(1, Number(input.value) + Number(productQuantity.dataset.productQuantity));
      return;
    }
    const cartPageQuantity = event.target.closest('[data-cart-page-quantity]');
    if (cartPageQuantity) {
      const input = cartPageQuantity.closest('.quantity-control').querySelector('input');
      input.value = Math.max(0, Number(input.value) + Number(cartPageQuantity.dataset.cartPageQuantity));
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !event.submitter || !event.submitter.matches('[data-ajax-add]')) return;
    event.preventDefault();
    addToCart(form);
  });

  document.addEventListener('change', (event) => {
    const option = event.target.closest('[data-option-select]');
    if (option) updateVariant(option.closest('variant-selects'));
    const cartInput = event.target.closest('[data-cart-quantity]');
    if (cartInput) {
      const item = cartInput.closest('[data-cart-item]');
      if (item) changeCartLine(Number(item.dataset.line), Math.max(0, Number(cartInput.value)));
    }
    const sort = event.target.closest('[data-sort-select]');
    if (sort) sort.form.submit();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && state.activeDrawer) closeDrawer();
    trapFocus(event);
  });

  document.querySelectorAll('product-recommendations').forEach(loadRecommendations);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('product-recommendations').forEach(loadRecommendations);
  });
})();
